import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { CrmPipelineDeal, CrmUnderwritingSnapshot } from '@bcc/shared';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { CrmToolsDialog } from '../../components/crm/CrmToolsDialog';

export function CrmPipelineDealDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token;
  const [deal, setDeal] = useState<CrmPipelineDeal | null>(null);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [snapshotLabel, setSnapshotLabel] = useState('');
  const [toolsOpen, setToolsOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !id) return;
    void (async () => {
      try {
        const r = await apiFetch<{ deal: CrmPipelineDeal }>(`/api/crm/pipeline-deals/${id}`, { token });
        setDeal(r.deal);
        setName(r.deal.name);
        setNotes(r.deal.notes ?? '');
      } catch {
        setErr('Deal not found.');
      }
    })();
  }, [token, id]);

  async function saveMeta(e: FormEvent) {
    e.preventDefault();
    if (!token || !id) return;
    const r = await apiFetch<{ deal: CrmPipelineDeal }>(`/api/crm/pipeline-deals/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify({ name: name.trim(), notes: notes.trim() || null }),
    });
    setDeal(r.deal);
  }

  async function saveSnapshot(e: FormEvent) {
    e.preventDefault();
    if (!token || !id || !snapshotLabel.trim()) return;
    const r = await apiFetch<{ deal: CrmPipelineDeal }>(`/api/crm/pipeline-deals/${id}/snapshots`, {
      method: 'POST',
      token,
      body: JSON.stringify({ label: snapshotLabel.trim() }),
    });
    setDeal(r.deal);
    setSnapshotLabel('');
  }

  async function applyUnderwriting(uw: Record<string, unknown>) {
    if (!token || !id) return;
    const r = await apiFetch<{ deal: CrmPipelineDeal }>(`/api/crm/pipeline-deals/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify({ underwriting: uw }),
    });
    setDeal(r.deal);
  }

  async function remove() {
    if (!token || !id || !window.confirm('Delete this pipeline deal?')) return;
    await apiFetch(`/api/crm/pipeline-deals/${id}`, { method: 'DELETE', token });
    nav('/admin/crm/deals');
  }

  if (err) return <p className="text-sm text-red-400">{err}</p>;
  if (!deal) return <p className="text-sm text-[var(--home-muted-on-dark)]">Loading…</p>;

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <Link to="/admin/crm/deals" className="text-xs text-[var(--home-muted-on-dark)] hover:text-[var(--home-miami-blue)]">
          ← Pipeline deals
        </Link>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setToolsOpen(true)}
            className="rounded-xl bg-[var(--home-miami-blue)] px-4 py-2 text-sm font-semibold text-[var(--home-zone-void)] transition hover:brightness-105"
          >
            Underwriting tools
          </button>
          <button
            type="button"
            onClick={() => void remove()}
            className="px-3 text-xs text-red-400 hover:text-red-300"
          >
            Delete
          </button>
        </div>
      </div>

      <CrmToolsDialog
        open={toolsOpen}
        onClose={() => setToolsOpen(false)}
        initialMerged={deal.underwriting}
        onApply={(uw) => void applyUnderwriting(uw)}
      />

      <h1 className="text-2xl font-semibold text-[var(--home-text-on-dark)]">Underwriting details</h1>

      <form onSubmit={(e) => void saveMeta(e)} className={`space-y-4 p-6 ${SITE_GLASS_CARD}`}>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Deal name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`w-full ${SITE_FORM_CONTROL}`}
          />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className={`w-full ${SITE_FORM_CONTROL}`}
          />
        </label>
        <button
          type="submit"
          className="rounded-xl border border-white/[0.12] bg-white/[0.08] px-5 py-2 text-sm font-medium text-[var(--home-text-on-dark)] transition hover:bg-white/[0.12]"
        >
          Save name & notes
        </button>
      </form>

      <section className={`space-y-3 p-6 ${SITE_GLASS_CARD}`}>
        <h2 className="text-sm font-semibold text-[var(--home-text-on-dark)]">Saved underwriting JSON</h2>
        <pre className="max-h-64 overflow-auto rounded-xl border border-white/[0.08] bg-neutral-950/60 p-4 text-xs text-[var(--home-muted-on-dark)]">
          {JSON.stringify(deal.underwriting, null, 2)}
        </pre>
      </section>

      <section className={`space-y-4 p-6 ${SITE_GLASS_CARD}`}>
        <h2 className="text-sm font-semibold text-[var(--home-text-on-dark)]">Snapshots</h2>
        <form onSubmit={(e) => void saveSnapshot(e)} className="flex flex-wrap items-end gap-2">
          <label className="min-w-[12rem] flex-1 space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Label</span>
            <input
              value={snapshotLabel}
              onChange={(e) => setSnapshotLabel(e.target.value)}
              placeholder="Q1 base case"
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>
          <button
            type="submit"
            className="rounded-xl bg-[var(--home-miami-blue)] px-5 py-2 text-sm font-semibold text-[var(--home-zone-void)] transition hover:brightness-105"
          >
            Save snapshot
          </button>
        </form>
        <ul className="space-y-2 text-sm">
          {[...deal.snapshots].reverse().map((s: CrmUnderwritingSnapshot, i: number) => (
            <li
              key={`${s.saved_at}-${i}`}
              className="rounded-lg border border-white/[0.06] px-3 py-2 text-[var(--home-text-on-dark)]"
            >
              <span className="font-medium">{s.label}</span>
              <span className="ml-2 text-xs text-[var(--home-muted-on-dark)]">
                {new Date(s.saved_at).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
